import cs from 'classnames';
import React from 'react';
import { ReactComponent as Expander } from '../../../../Icons/Expander.svg';
import { ReactComponent as BackArrow } from '../../../../Icons/BackArrow.svg';
import { injectStoresV1 } from '../../../stores/injectStoresV1';
import { CategoryModel } from '../../../model/CategoryModel';
import { CategoriesStore } from '../../../stores/CategoriesStore';
import { InteractionsStore } from '../../../stores/InteractionsStore';
import { ScreenStore } from '../../../stores/ScreenStore';
import { RoutingStore } from '../../../stores/RoutingStore';
import { startCategoryInteraction } from '../../../utils/fedops-logger';
import { SeoLink } from '../../SeoLink/SeoLink';
import { SubCategory } from '../SubCategory/SubCategory';
import s from '../Categories.scss';
import { WithTranslations, withTranslations } from '../../../../utils/withTranslations';
import { biLogger } from '../../../utils/BILogger';

export interface CategoryProps extends WithTranslations {
  categoryId?: string;
  dataHook: string;
  className: string;
  title: string;
  items?: CategoryModel[];
  noIcon?: boolean;
  isActive?: boolean;
  expanded?: boolean;
  categoriesStore: CategoriesStore;
  interactionsStore: InteractionsStore;
  screenStore: ScreenStore;
  routingStore: RoutingStore;
  setActiveRef?: (HTMLElement) => void;
}

export class CategoryCmp extends React.Component<CategoryProps> {
  public render() {
    const props = this.props;
    const { t, setActiveRef, noIcon, isActive, categoryId } = props;

    const isEmpty = this.isEmpty();
    const categoryLink = props.routingStore.routes.categoryRoute.get(categoryId);
    const title = categoryId === 'all' ? t('sideBar.seeAllTemplates') : props.title;

    return (
      <li
        className={props.className}
        data-hook={props.dataHook}
        data-bi-element="category"
        data-bi-element-value={title}
      >
        <input
          className={s.checkbox}
          data-hook="expanded"
          type="checkbox"
          checked={isEmpty ? false : props.expanded}
          disabled={isEmpty}
          aria-disabled={isEmpty}
          id={`toggle-category-${categoryId}`}
          onChange={this.handleCheckChange}
        />
        <label
          role="button"
          data-hook="toggle"
          className={cs(s.label, { [s.noLabel]: props.noIcon })}
          tabIndex={0}
          aria-disabled={isEmpty}
          aria-expanded={props.expanded}
          aria-label={t('sideBar.toggleCategory', { category: title })}
          onClick={this.handleToggle}
          onMouseDown={this.handleToggleMouseDown}
          htmlFor={`toggle-category-${categoryId}`}
        >
          <Expander fill="currentColor" width="1em" height="1em" />
        </label>
        <SeoLink
          className={cs(s.link, {
            [s.noIcon]: noIcon,
            [s.active]: isActive,
          })}
          data-hook="link"
          to={categoryLink}
          onClick={this.handleLinkClick}
          aria-current={props.isActive}
          innerRef={props.isActive ? setActiveRef : undefined}
        >
          <span className={s.linkText} data-hook="link-title">
            {title}
          </span>
          {this.props.noIcon ? <BackArrow fill="currentColor" width="1em" height="1em" /> : null}
        </SeoLink>
        {props.noIcon || !props.items || !props.items.length ? null : (
          <ul className={s.subItems}>
            <li className={s.mobileCategoryLabel}>
              <span className={s.mobileLinkText} data-hook="subMenuCategoryTitle">
                {title}
              </span>
            </li>
            <li className={s.mobileCategoryLink}>
              <SeoLink
                className={cs({ [s.active]: isActive })}
                data-hook="subMenuCategoryLink"
                to={categoryLink}
                aria-current={props.isActive}
                innerRef={props.isActive ? setActiveRef : undefined}
                onClick={() => biLogger.logCategorySelect({ category: categoryId })}
              >
                <span className={s.mobileLinkText}>{t('sideBar.mobile.mainCategory.all')}</span>
              </SeoLink>
            </li>
            {props.items.map((subItem) => {
              const isActive = subItem === props.categoriesStore.activeSubCategory;
              const subCategoryLink = props.routingStore.routes.subCategoryRoute.get(categoryId, subItem.id);
              return (
                <SubCategory
                  key={subItem.id}
                  dataHook="subCategory"
                  className={s.listSubItem}
                  isActive={isActive}
                  setRef={isActive && setActiveRef}
                  title={subItem.title}
                  to={subCategoryLink}
                  onClick={() => biLogger.logCategorySelect({ category: categoryId, subCategory: subItem.id })}
                />
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  private handleCheckChange = (e): void => {
    if (this.props.items.length > 0) {
      const checked = e.target.checked;
      this.props.categoriesStore.toggleCategory(this.props.categoryId, checked);
    }
  };

  private handleToggle = (e): void => {
    if (!e.key || ['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      if (this.props.items.length > 0) {
        this.props.categoriesStore.toggleCategory(this.props.categoryId);
      }
    }
  };

  private handleToggleMouseDown = (e): void => {
    e.preventDefault();
  };

  private handleLinkClick = (): void => {
    biLogger.logCategorySelect({ category: this.props.categoryId });
    this.props.interactionsStore.initInteraction('category_switch');
    startCategoryInteraction();
  };

  private isEmpty(): boolean {
    return this.props.items.length === 0 || this.props.noIcon;
  }
}

export const Category = withTranslations(
  injectStoresV1('categoriesStore', 'screenStore', 'interactionsStore', 'routingStore')(CategoryCmp),
);
